import React, { useState, useEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import { useHistory, useLocation } from 'react-router-dom';

import Input from '../../components/passwordInput/PasswordInput';
import { validadeEmail, validadePhone } from '../../utils/validation';
import services from '../../services/api';
import {
  getSessionUser,
  openClientSession,
} from '../../services/sessionService';
import { Label } from '../../components/Label';
import { Button } from '../../components/Button';
import { InputText } from '../../components/InputText';
import { MaskedInput } from '../../components/MaskedInput';

// Tempo para o toast de sucesso aparecer antes da troca de tela.
const REDIRECT_DELAY_MS = 600;

export function Login({ handleOption }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [redirect, setRedirect] = useState('');
  const [loginMask, setLoginMask] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    checkURL();
    checkLoggedUser();
  }, []);

  const handleFormatLogin = (value) => {
    if (validadePhone(value.replace(/[\D]/g, ''))) {
      return value.replace(/[\D]/g, '');
    }
    return value.replace(/\s/g, '').toLowerCase();
  };

  const getLoginMask = (value) => {
    if (validadePhone(value.replace(/[^\d]/g, ''))) {
      if (value.replace(/[^\d]/g, '').length === 10) {
        return '(99) 9999-9999';
      }
      return '(99) 99999-9999';
    }
    return '';
  };

  useEffect(() => {
    setLoginMask(getLoginMask(login));
  }, [login]);

  // A sessao e um cookie HttpOnly: so GET /session sabe se ha cliente logado.
  const checkLoggedUser = async () => {
    try {
      const user = await getSessionUser();
      if (user) {
        localStorage.setItem('NOME_USUARIO', user.name);
        history.push('/client/dashboard');
      }
    } catch (err) {
      // sem sessao ou API fora do ar: segue na tela de login
    }
  };

  const checkURL = () => {
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');
    if (redirectParam) {
      setRedirect(redirectParam);
    }
  };

  // TODO: Implementar hook de login em vez de chamar diretamente a rota
  const performLogin = async (login, password) => {
    try {
      const response = await services.api.post(
        services.config.endpoint + 'users/login',
        {
          login: handleFormatLogin(login),
          password,
        },
      );
      return response.data;
    } catch (err) {
      const status = err && err.response && err.response.status;
      // Sem resposta ou erro do servidor nao e senha errada: a mensagem precisa dizer o que aconteceu.
      if (!status || status >= 500) {
        toastr.error(
          'Não foi possível entrar agora',
          'Verifique sua conexão e tente de novo em instantes.',
        );
      } else {
        toastr.warning(
          'Dados incorretos',
          'Confira o e-mail ou celular e a senha e tente de novo.',
        );
      }
      return null;
    }
  };

  const handleLoginResponse = (responseData) => {
    if (!responseData) {
      setSubmitting(false);
      return;
    }

    // Senha provisória dada pelo suporte: cria a senha nova antes de entrar.
    if (responseData.mustChangePassword) {
      toastr.info('Crie uma senha nova', 'A senha que você usou é provisória.');
      window.location.href = `/client/login?token=${encodeURIComponent(responseData.token)}`;
      return;
    }

    openClientSession(responseData);

    // O botao segue em "Entrando..." ate a troca de tela.
    const isValidData = validateData(responseData);
    if (!isValidData) {
      localStorage.removeItem('USER_CONFIRMED'); // Ensure it's not set if data is invalid
      setTimeout(() => history.push('/client/confirmation-data'), REDIRECT_DELAY_MS);
      return;
    }

    localStorage.setItem('USER_CONFIRMED', 'true'); // Set flag if data is valid
    toastr.success('Login efetuado com sucesso!');
    const target = redirect !== '' ? `/client/${redirect}` : '/client/dashboard';
    setTimeout(() => history.push(target), REDIRECT_DELAY_MS);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitting || isSubmitDisabled) return;
    setSubmitting(true);
    const responseData = await performLogin(login, password);
    handleLoginResponse(responseData);
  };

  const validateData = (userData) => {
    if (
      !userData.cpfCnpj ||
      !userData.phone ||
      !userData.email ||
      !userData.isAcceptedTerms
    ) {
      return false;
    }
    return true;
  };

  const isSubmitDisabled =
    !login ||
    !password ||
    password.length < 6 ||
    (!validadeEmail(login) && !validadePhone(login.replace(/[^\d]/g, '')));

  return (
    <div className="auth-form">
      <header className="auth-form-header">
        <h2>Entrar na minha garagem</h2>
        <p>Veja tudo o que já foi feito no seu veículo.</p>
      </header>
      <form onSubmit={handleSubmit}>
        <div className="auth-field">
          <Label htmlFor="login-user">E-mail ou celular</Label>
          <MaskedInput
            id="login-user"
            mask={loginMask}
            value={login}
            autoComplete="username"
            placeholder="seu@email.com ou (00) 00000-0000"
            onChange={(event) => setLogin(event.target.value)}
          />
        </div>
        <div className="auth-field">
          <div className="auth-field-row">
            <Label htmlFor="login-password">Senha</Label>
            <button
              type="button"
              className="auth-link"
              onClick={() => handleOption(1)}
            >
              Esqueci minha senha
            </button>
          </div>
          <InputText
            id="login-password"
            autoComplete="new-password"
            type="password"
            className="form-control foco-input"
            placeholder="Mínimo de 6 caracteres"
            onChange={(event) =>
              setPassword(event.target.value.replace(/\s/g, ''))
            }
            value={password}
            maxLength="18"
          />
        </div>
        <Button
          type="submit"
          className="auth-submit"
          disabled={isSubmitDisabled || submitting}
          aria-busy={submitting}
          color="green400"
        >
          {submitting ? (
            <>
              <span className="auth-spinner" aria-hidden="true" />
              Entrando…
            </>
          ) : (
            'Entrar'
          )}
        </Button>
        <p className="auth-signup">
          Não tem conta?{' '}
          <button
            type="button"
            className="auth-link"
            onClick={() => history.push('signup')}
          >
            Cadastre-se agora
          </button>
        </p>
      </form>
    </div>
  );
}

export default Login;
